import { FilterOperators } from "@crispico/foundation-gwt-js";
import { FieldDescriptor, Utils } from "@crispico/foundation-react";
import { Filter } from "@crispico/foundation-react/components/CustomQuery/Filter";
import { ModalExt } from "@crispico/foundation-react/components/ModalExt/ModalExt";
import DateFieldRenderer from "@crispico/foundation-react/entity_crud/fieldRenderers/DateFieldRenderer";
import StringFieldRenderer from "@crispico/foundation-react/entity_crud/fieldRenderers/StringFieldRenderer";
import { ShareLinkLogic } from "@crispico/foundation-react/entity_crud/ShareLinkLogic";
import { getHistoryGraphColor } from "@crispico/foundation-react/pages/Audit/historyGraphItem/HistoryGraphItem";
import { DetectionEventForMap, DetectionEventForMap_detectedBy } from "apollo-gen/DetectionEventForMap";
import { EquipmentResourceForMap } from "apollo-gen/EquipmentResourceForMap";
import { detectionEventEntityDescriptor } from "AppEntityDescriptors";
import { equipmentResourceEntityDescriptor } from "pages/EquipmentResource/equipmentResourceEntityDescriptor";
import React from "react";
import { NavLink } from "react-router-dom";
import { Button, Checkbox, Dimmer, DimmerDimmable, Icon, Input, Label, List, ListItem, Loader, Message, Modal, ModalActions, Segment } from "semantic-ui-react";
import { DetectionEventsOptions } from "./RealTimeMap";
import { PopupWithHelpTooltip } from "@crispico/foundation-react/components/semanticUiReactExt";

interface DetectionEventsPerEquipmentResource {
    index: number,
    entity: DetectionEventForMap_detectedBy;
    color: any;
    events: DetectionEventForMap[];
}

export type MapDetectionEventsModalProps = {
    equipmentResource: EquipmentResourceForMap;
    events: { list: DetectionEventForMap[], displayedIds: any[] };
    options: DetectionEventsOptions;
    onCheckDisplayEventId: (checked: boolean, eventId: any, equipmentResourceId: any) => void;
    onOptionsChanged: (newOptions: DetectionEventsOptions) => void;
    onReapplyRecommendedFilters: (equipmentResourceId: any) => void;
    onRefresh: (equipmentResourceId: any) => void;
}

export class MapDetectionEventsModal extends React.Component<MapDetectionEventsModalProps> {

    constructor(props: MapDetectionEventsModalProps) {
        super(props);
        this.onClose = this.onClose.bind(this);
    }

    onClose() {
        this.props.onOptionsChanged({ ...this.props.options, showModal: false })
    }

    renderContent() {
        const { props } = this;
        let erMap: { [key: string]: DetectionEventsPerEquipmentResource } = {};
        this.props.events.list.forEach((event) => {
            const id = event.detectedBy.id + "";
            if (!erMap[id]) {
                const index = Object.keys(erMap).length + 1;
                erMap[id] = {
                    index: index,
                    entity: event.detectedBy,
                    color: getHistoryGraphColor(index),
                    events: []
                }
            }
            erMap[id].events.push(event);
        })
        return <>
            <Segment compact className="flex-container gap10 less-padding  no-margin">
                <div className="flex-container-row flex-center  flex-justify-content-space-between flex-grow gap5">
                    <NavLink to={
                        new ShareLinkLogic().createLink(false, detectionEventEntityDescriptor, Filter.createForClient('id', FilterOperators.forNumber.in, this.props.events?.list.map(event => event.id).join(","))
                        )}
                    >{_msg("dto_crud.goToTable")}</NavLink>
                    <div>
                        <Button onClick={() => props.onReapplyRecommendedFilters(props.equipmentResource.id)}>{_msg("MapDetectionEventsModal.reapply")}</Button>
                        <PopupWithHelpTooltip tooltip="Filters out events 1/ older with 1h than the most recent event or 2/ with same location, same detected by resource or 3/ same detected by resource and diff time between them < 30s" />
                    </div>
                </div>
                <div className="flex-container-row flex-center flex-grow gap10">
                    <Checkbox label={_msg("MapDetectionEventsModal.drawPolygon")}
                        checked={props.options.drawPolygon}
                        onChange={(e, data) => props.onOptionsChanged({ ...props.options, drawPolygon: data.checked! })}></Checkbox>                    
                    <Checkbox label={_msg("MapDetectionEventsModal.tooltipsShowAll")} disabled={!props.options.drawPolygon}
                        checked={props.options.showAllTooltips}
                        onChange={(e, data) => props.onOptionsChanged({ ...props.options, showAllTooltips: data.checked! })}></Checkbox>                    
                </div>    
                <div className="flex-container-row flex-center flex-grow gap10">                   
                    <Checkbox label={_msg("MapDetectionEventsModal.drawCircle")} disabled={!props.options.drawPolygon}
                        checked={props.options.drawCircle}
                        onChange={(e, data) => props.onOptionsChanged({ ...props.options, drawCircle: data.checked! })}></Checkbox>    
                    <span><Input className="small-margin-left" style={{ width: "70px" }} disabled={!props.options.drawPolygon}
                        defaultValue={props.options.circleRadius}
                        size="mini" type="number"
                        onChange={(e, data) => {
                            let newValue = Number(data.value);
                            // newValue = newValue < 1 ? 1 : newValue > 20 ? 20 : newValue;
                            props.onOptionsChanged({ ...props.options, circleRadius: newValue });
                        }} /></span>                                 
                </div>             
                <div className="flex-container-row flex-center gap5 flex-justify-content-end">
                    <span>{_msg("MapDetectionEventsModal.nbEvents")} <Input className="small-margin-left" style={{ width: "70px" }}
                        defaultValue={props.options.nbLastDetectionEvents}
                        size="mini" type="number"
                        onChange={(e, data) => {
                            let newValue = Number(data.value);
                            newValue = newValue < 1 ? 1 : newValue > 20 ? 20 : newValue;
                            props.onOptionsChanged({ ...props.options, nbLastDetectionEvents: newValue });
                        }} /></span>
                    <Button positive onClick={() => props.onRefresh(props.equipmentResource.id)}><Icon name="refresh"/>{_msg("general.refresh")}</Button>
                </div>
            </Segment>
            <Segment compact className="flex-container-row flex-wrap flex-center gap5 less-padding no-margin" style={{ maxHeight: "150px", overflowY: "auto" }}>
                {_msg("EquipmentResource.detectedBy.label")}
                {Object.values(erMap).sort((a, b) => a.index - b.index).map(data => {
                    return <div className="flex-container-row">
                        <Label circular horizontal style={{ backgroundColor: data.color, color: "white" }}>{data.index}</Label>
                        {equipmentResourceEntityDescriptor.getField("identifier").renderField(data.entity, FieldDescriptor.castAdditionalFieldRendererProps(StringFieldRenderer, { asLink: true }))}
                    </div>
                })}
            </Segment>
            <Segment compact className="flex-container-row flex-wrap gap3 less-padding no-margin" style={{ maxHeight: "200px", overflowY: "auto" }}>
                {props.events.list.map(event => {
                    const erData = erMap[event.detectedBy.id];
                    return <Label className="flex-container-row flex-center gap3">
                        <Checkbox checked={props.events.displayedIds.findIndex(id => id === event.id) !== -1}
                            onChange={(e, data) => props.onCheckDisplayEventId(data.checked!, event.id, props.equipmentResource.id)} />
                        <Label circular horizontal style={{ backgroundColor: erData.color, color: "white" }}>{erData.index}</Label>
                        {detectionEventEntityDescriptor.getField("date").renderField(event, FieldDescriptor.castAdditionalFieldRendererProps(DateFieldRenderer, { format: Utils.dateTimeFormatShorter, formatForToday: Utils.timeWithSecFormat }))}
                    </Label>
                })}
            </Segment>
        </>
    }

    render() {
        return <ModalExt size='tiny' key="derivingEventsList" transparentDimmer open closeIcon={true}
            onClose={this.onClose} >
            <Modal.Header as="h3">{_msg("MapDetectionEventsModal.title", this.props.equipmentResource.identifier)}</Modal.Header>
            <Modal.Content className="less-padding gap5" >
                <DimmerDimmable as={Segment} className="flex-container gap5 basic no-padding" style={{ minHeight: "200px" }}>
                    <Dimmer active={this.props.options.loadingData}>
                        <Loader size='large'>{_msg("general.loading")}</Loader>
                    </Dimmer>
                    {!this.props.options.loadingData
                        ? (this.props.events.list.length === 0
                            ? <Message negative compact className="less-padding no-margin" >
                                {_msg("MapDetectionEventsModal.noData")}
                            </Message>
                            : this.renderContent())
                        : <></>}
                </DimmerDimmable>
            </Modal.Content>
            <ModalActions>
                <Button onClick={this.onClose}>{_msg("general.close")}</Button>
            </ModalActions>
        </ModalExt>
    }
}